import * as d3 from 'd3'

import { ObjectHash } from '../../types/shared'
import Line from '../canvas/line'
import Text from '../canvas/text'
import {
  INDICATOR_LABEL_HEIGHT,
  IndicatorType,
  MOBILE_VIEW_BREAKPOINT_WIDTH,
  OFFSET,
  PADDING,
  TextAlign,
  TextBaseline,
} from '../constants/common'
import { getTranslate } from '../controllers/renderUtils'
import Pane from '../models/pane'
import Quote from '../models/quote'
import { getSanitizedTicker } from '../utils'
import { getInstrument } from '../utils/chart'
import { getHEXWithSpecificAplha } from '../utils/colors'
import Indicator from './indicator'

interface Attrs {
  period: string
  tickers?: string
}

const DEFAULT_TICKERS = 'SPY,QQQ'

class PricePerformance extends Indicator<Attrs> {
  static type = IndicatorType.Perf
  static label = 'Price Performance %'

  static getNumOfBarsBuffer() {
    return 0
  }

  timeframe: string
  tickers = ''
  ready = false
  firstFetchCalled = false
  first: ObjectHash = {}
  perf: ObjectHash = {}
  quotes: ObjectHash<Quote> = {}
  indicatorLabelWidth: ObjectHash = {}

  constructor(attrs: Attrs, model: Pane) {
    super(attrs, model)
    this.renderYAxis = this.renderYAxis.bind(this)

    this.timeframe = this.model.chart().timeframe
    this.fy = d3.scaleLinear().range([0, this.contentHeight])
    if (this.attrs.period && !this.tickers) {
      this.tickers = this.attrs.period
    }
  }

  async compute() {
    const tickers = this.getTickers()
    const chartUuid = this.model.chart().chart_layout().uuid
    const hasCachedData = this.getTickersAndTimeframe().every(
      ({ ticker, instrument, timeframe }) =>
        Quote.select(
          (q: Quote) =>
            q.ticker === ticker &&
            q.instrument === instrument &&
            q.timeframe === timeframe &&
            chartUuid === q.chartUuid &&
            !q.isFetching
        ).length > 0
    )

    if (this.timeframe !== this.model.chart().quote().timeframe || (!this.ready && !this.firstFetchCalled)) {
      this.timeframe = this.model.chart().quote().timeframe
      this.firstFetchCalled = true

      if (!hasCachedData) {
        const data = await Quote.getAll(this.getTickersAndTimeframe())
        if (data) {
          this._compute(tickers, data)
          this.trigger('change')
        }
      } else {
        this.ready = true
        this.compute()
      }
    } else if (this.ready) {
      const data: ObjectHash<Quote> = {}
      for (const ticker of tickers) {
        data[ticker] = Quote.select(
          (q: Quote) => q.ticker === ticker && q.timeframe === this.timeframe && chartUuid === q.chartUuid
        )[0]
      }
      this._compute(tickers, data)
    }
  }

  _compute(tickers: string[], data: ObjectHash<Quote>) {
    const { leftOffset } = this.model.chart()
    let firstBarTime = null
    let lastBarTime = null
    for (let i = 0; i < this.data.close.length; i++) {
      const center = this.fx(i)
      if (center + leftOffset < 0 || center + leftOffset > this.contentWidth) {
        // margin?
        continue
      }

      if (firstBarTime === null) {
        firstBarTime = this.data.date[i]
      }

      lastBarTime = this.data.date[i]
    }

    data[this.data.ticker] = this.data
    this.quotes = data
    this.perf = {}
    const dateToIndex = this.data.getDateToIndex()
    for (const ticker of [this.data.ticker, ...tickers]) {
      this.perf[ticker] = []
      const d = data[ticker]
      if (!d) {
        continue
      }
      let first = null
      for (let i = 0; i < d.close.length; i++) {
        const date = d.date[i]
        if (date < firstBarTime! || date > lastBarTime!) {
          continue
        }

        const index = dateToIndex[date]
        if (index === undefined || index === null) {
          continue
        }

        if (first === null) {
          first = d.close[i]
          this.first[ticker] = first
        }

        this.perf[ticker][index] = (d.close[i] * 100) / first - 100
      }
    }

    const perfArrays = Object.keys(this.perf).map((x) => this.perf[x])
    const { min, max } = perfArrays.some((array) => array.length > 0)
      ? this.computeVisibleMinMax(...perfArrays)
      : this.getDomainDefaults(this.type)

    this.min = min
    this.max = max

    const fy = d3
      .scaleLinear()
      .range([0, this.contentHeight])
      .domain([this.model.scaleRange?.max ?? this.max, this.model.scaleRange?.min ?? this.min])
    if (!this.model.scaleRange) {
      fy.nice(10)
    }
    // @ts-expect-error - TODO scale.y types dont match
    this.model.scale.y = this.fy = fy
    this.ready = true
  }

  renderIndicator(context: CanvasRenderingContext2D) {
    if (!this.ready) {
      return
    }
    const { ColorsSettings, IndicatorSettings } = this.getChartLayoutSettings()
    new Line(
      {
        x1: -this.leftOffset,
        x2: -this.leftOffset + this.contentWidth,
        y1: Math.round(this.fy(0)),
        y2: Math.round(this.fy(0)),
        strokeStyle: '#ff8787',
        dashLength: 3,
      },
      this.model
    ).render(context)

    context.translate(0.5, 0.5)
    const iterable = this.getTickers()
    for (let index = 0; index < iterable.length; index++) {
      const ticker = iterable[index]
      if (this.perf[ticker].length > 0) {
        context.set('strokeStyle', ColorsSettings[index % ColorsSettings.length])
        context.beginPath()
        for (let i = 0; i < this.perf[ticker].length; i++) {
          const y = Math.round(this.fy(this.perf[ticker][i]))
          context.lineTo(this.fx(i), y)
        }
        context.stroke()
      }
    }

    context.set('strokeStyle', IndicatorSettings.general.Colors.line)
    context.beginPath()
    for (let i = 0; i < this.perf[this.data.ticker].length; i++) {
      const y = Math.round(this.fy(this.perf[this.data.ticker][i]))
      context.lineTo(this.fx(i), y)
    }
    context.stroke()
    context.translate(-0.5, -0.5)
  }

  renderCrossTextQuotePage(context: CanvasRenderingContext2D, crossIndex: number) {
    if (!context || !crossIndex) {
      return
    }
    const { ColorsSettings, ChartSettings, IndicatorSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    const tickers = [this.data.ticker, ...this.getTickers()]
    tickers.forEach((key, index) => {
      const crossIndexTickerTimeStamp = this.quotes?.[this.data.ticker]?.getDataByBarIndex('date', crossIndex)
      const tickerDateToIndex = crossIndexTickerTimeStamp
        ? this.quotes?.[key]?.getDateToIndex()?.[crossIndexTickerTimeStamp]
        : null
      const tickerPerformance = tickerDateToIndex
        ? (this.quotes?.[key]?.close?.[tickerDateToIndex] * 100) / this.first[key] - 100
        : null
      if (tickerPerformance !== null && !isNaN(tickerPerformance)) {
        context.fillStyle = Colors.canvasFill
        context.fillRect(
          IndicatorSettings.left.indicatorLabel.margin.left! - PADDING.XXXS,
          (IndicatorSettings.left.indicatorLabel.font.lineHeight! +
            IndicatorSettings.left.indicatorLabel.labelSpacing) *
            (index + 1) +
            IndicatorSettings.left.indicatorLabel.margin.top! -
            PADDING.XXXS,
          PADDING.XXXS + this.indicatorLabelWidth[key] + PADDING.XXXS,
          PADDING.XXXS + IndicatorSettings.left.indicatorLabel.font.size! + PADDING.XXXS
        )
        new Text(
          {
            text: `${key} ${tickerPerformance.toFixed(2)}%`,
            x: IndicatorSettings.left.indicatorLabel.margin.left,
            y:
              (IndicatorSettings.left.indicatorLabel.font.lineHeight! +
                IndicatorSettings.left.indicatorLabel.labelSpacing) *
                (index + 1) +
              IndicatorSettings.left.indicatorLabel.margin.top!,
            font: Text.getMergedPropsWithDefaults('font', IndicatorSettings.left.indicatorLabel.font),
            fillStyle:
              index === 0 ? Colors.performanceIndicatorLabel : ColorsSettings[index - (1 % ColorsSettings.length)],
            textAlign: TextAlign.left,
            textBaseline: TextBaseline.top,
          },
          this.model
        ).render(context)
      }
    })
  }

  renderCrossTextChartsPage(context: CanvasRenderingContext2D, crossIndex: number) {
    if (!context || !crossIndex) {
      return
    }

    const { ColorsSettings, ChartSettings, IndicatorSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    let isVertical = true
    const labelBottom = IndicatorSettings.left.indicatorLabel.margin.top! + INDICATOR_LABEL_HEIGHT
    const tickers = [this.data.ticker, ...this.getTickers()]
    const tickerText = new Text(
      {
        font: Text.getMergedPropsWithDefaults('font', {
          ...IndicatorSettings.left.indicatorLabel.font,
          style: 'normal',
        }),
        textAlign: TextAlign.left,
        textBaseline: TextBaseline.middle,
        background: getHEXWithSpecificAplha(Colors.canvasFill, 0.8),
        padding: { top: 0, bottom: 0, left: 5, right: 5 },
      },
      this.model
    )
    let x = IndicatorSettings.left.indicatorLabel.margin.left!
    let y =
      IndicatorSettings.left.indicatorLabel.margin.top! + (2 * INDICATOR_LABEL_HEIGHT - tickerText.attrs.lineHeight) / 2

    const maxHeight = tickerText.attrs.lineHeight * tickers.length

    const canFitItemsVertically = maxHeight + labelBottom <= this.height
    if (this.width <= MOBILE_VIEW_BREAKPOINT_WIDTH || !canFitItemsVertically) {
      isVertical = false
      x += x + this.labelWidth
    } else {
      y += IndicatorSettings.left.indicatorLabel.margin.top! + INDICATOR_LABEL_HEIGHT
    }

    const tickerQuote = this.quotes?.[this.data.ticker]
    const maxCrossIndex = Math.min(crossIndex, (tickerQuote?.close.length ?? Infinity) - 1)
    tickers.forEach((key, index) => {
      const crossIndexTickerTimeStamp = tickerQuote?.getDataByBarIndex('date', maxCrossIndex)
      const tickerDateToIndex = crossIndexTickerTimeStamp
        ? this.quotes?.[key]?.getDateToIndex()?.[crossIndexTickerTimeStamp]
        : null
      const tickerPerformance = tickerDateToIndex
        ? (this.quotes?.[key]?.close?.[tickerDateToIndex] * 100) / this.first[key] - 100
        : null
      if (tickerPerformance !== null && !isNaN(tickerPerformance)) {
        tickerText.set({
          x,
          y,
          fillStyle:
            index === 0 ? Colors.performanceIndicatorLabel : ColorsSettings[index - (1 % ColorsSettings.length)],
          text: `${key} ${tickerPerformance.toFixed(2)}%`,
        })
        const tickerTextWidth =
          tickerText.measure(context) + tickerText.attrs.padding.left + tickerText.attrs.padding.right
        if (
          !isVertical &&
          this.width - IndicatorSettings.left.indicatorLabel.margin.left! - IndicatorSettings.right.width! <=
            x + tickerTextWidth
        ) {
          x = 2 * IndicatorSettings.left.indicatorLabel.margin.left! + this.labelWidth
          y += tickerText.attrs.lineHeight
          tickerText.set({ x, y })
        }
        tickerText.render(context)

        if (isVertical) {
          y += tickerText.attrs.lineHeight
        } else {
          x += tickerTextWidth
        }
      }
    })
  }

  renderXAxis(context: CanvasRenderingContext2D) {
    if (!this.ready) {
      return
    }
    return super.renderXAxis(context)
  }

  renderYAxis(context: CanvasRenderingContext2D) {
    let ticker
    super.renderYAxis(context)
    const { IndicatorSettings, ChartSettings, ColorsSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    if (!this.ready) {
      return
    }
    if (this.perf[this.data.ticker].length === 0) {
      return
    }
    const translate = getTranslate({
      context,
      xOffset: IndicatorSettings.left.width,
      yOffset: IndicatorSettings.top.height,
    })
    translate.do()
    const { lineHeight, padding, size } = IndicatorSettings.right.axis.font
    const text = new Text(
      {
        x: this.contentWidth + OFFSET.M - padding!.left! + IndicatorSettings.right.axis.margin.left!,
        font: Text.getMergedPropsWithDefaults('font', { size }),
        lineHeight,
        padding: {
          left: padding!.left!,
          right: padding!.right!,
          top: padding!.top!,
          bottom: padding!.bottom!,
        },
        textBaseline: TextBaseline.middle,
        fillStyle: Colors.crossText,
      },
      this.model
    )
    const iterable = this.getTickers()
    const textLabels = []
    for (let index = 0; index < iterable.length; index++) {
      ticker = iterable[index]
      if (this.perf[ticker].length > 0) {
        textLabels.push({
          text: this.perf[ticker].last().toFixed(2) + '%',
          y: Math.round(this.fy(this.perf[ticker].last())),
          background: ColorsSettings[index % ColorsSettings.length],
        })
      }
    }
    textLabels.push({
      text: this.perf[this.data.ticker].last().toFixed(2) + '%',
      y: Math.round(this.fy(this.perf[this.data.ticker].last())),
      background: Colors.performanceIndicatorLabel,
    })

    const minY = 0
    const maxY = this.contentHeight
    textLabels.forEach((labelSetting) => {
      if (labelSetting.y >= minY && labelSetting.y <= maxY) {
        text.set(labelSetting).render(context)
      }
    })

    translate.undo()
  }

  renderLabelQuotePage(context: CanvasRenderingContext2D) {
    super.renderLabelQuotePage(context)
    const { ColorsSettings, ChartSettings, IndicatorSettings } = this.getChartLayoutSettings()
    const { Colors } = ChartSettings.general
    if (this.ready) {
      const tickers = this.getTickers()
      const tickerPerformance = [
        { [this.data.ticker]: this.perf[this.data.ticker][this.perf[this.data.ticker].length - 1] },
        ...tickers.map((ticker) => ({ [ticker]: this.perf[ticker][this.perf[ticker].length - 1] })),
      ].reduce((prev, current) => ({ ...prev, ...current }), {})
      Object.keys(tickerPerformance).forEach((key, index) => {
        if (tickerPerformance[key]) {
          const text = new Text(
            {
              text: `${key} ${tickerPerformance[key].toFixed(2)}%`,
              x: IndicatorSettings.left.indicatorLabel.margin.left,
              y:
                (IndicatorSettings.left.indicatorLabel.font.lineHeight! +
                  IndicatorSettings.left.indicatorLabel.labelSpacing) *
                  (index + 1) +
                IndicatorSettings.left.indicatorLabel.margin.top!,
              font: Text.getMergedPropsWithDefaults('font', IndicatorSettings.left.indicatorLabel.font),
              fillStyle:
                index === 0 ? Colors.performanceIndicatorLabel : ColorsSettings[index - (1 % ColorsSettings.length)],
              textAlign: TextAlign.left,
              textBaseline: TextBaseline.top,
            },
            this.model
          )
          this.indicatorLabelWidth = { ...this.indicatorLabelWidth, [key]: text.measure(context) }
          text.render(context)
        }
      })
    }
  }

  getTickers() {
    return this.tickers
      .split(',')
      .map((ticker) => ticker)
      .filter((x) => x)
  }

  getTickersAndTimeframe() {
    const { timeframe } = this.model.chart().quote()
    return this.getTickers().map((ticker) => ({
      ticker,
      instrument: getInstrument(ticker),
      timeframe,
    }))
  }

  set(values: Partial<Attrs>) {
    this.ready = false
    this.firstFetchCalled = false
    super.set({ ...values, tickers: values.period || (values.tickers ? getSanitizedTicker(values.tickers, true) : '') })
    this.tickers = (this.tickers || '').toUpperCase()
  }

  getModalConfig() {
    return {
      title: this.label,
      inputs: [
        {
          type: 'text',
          label: 'Tickers',
          name: 'tickers',
          value: !!this.tickers ? this.tickers : DEFAULT_TICKERS,
          required: true,
        },
      ],
      inputsErrorMessages: {
        tickers: 'At least one ticker has to be provided',
      },
    }
  }

  getIsValid(key: string): boolean {
    switch (key) {
      case 'tickers':
        return this[key].length > 0
      default:
        return false
    }
  }

  toString() {
    return this.getIsChartPageSpecificFunctionality() ? 'PERF %' : `PERF% ${this.tickers}`
  }
}

export default PricePerformance
